import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { fastIpfsUrl } from "../lib/media";
import IpfsImage from "./IpfsImage";

/**
 * Lupin-style category card: dark background, neon purple border,
 * big image, "N PROD" badge top-right, name + circular arrow button at bottom.
 */
const CategoryCard = ({ category, onOpen, image, count, index = 0 }) => {
    const [loaded, setLoaded] = useState(false);
    const fastImg = fastIpfsUrl(image);
    return (
        <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: index * 0.05 }}
            className="relative w-full overflow-hidden rounded-2xl border border-[#7c3aed]/50 bg-[#0d0418] shadow-[0_0_22px_rgba(124,58,237,0.25)] hover:shadow-[0_0_30px_rgba(232,121,249,0.45)] hover:border-[#e879f9] group cursor-pointer aspect-[3/4]"
            onClick={onOpen}
            data-testid={`category-card-${category.slug}`}
        >
            {/* Skeleton shimmer while loading */}
            {fastImg && !loaded && (
                <div className="absolute inset-0 bg-gradient-to-br from-[#1a0b2e] via-[#0d0418] to-[#1a0b2e] animate-pulse" />
            )}
            {/* Background image */}
            {fastImg ? (
                <IpfsImage
                    src={fastImg}
                    alt={category.name}
                    loading={index < 2 ? "eager" : "lazy"}
                    fetchPriority={index < 2 ? "high" : "auto"}
                    decoding="async"
                    onLoad={() => setLoaded(true)}
                    className={`absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${loaded ? "opacity-100" : "opacity-0"}`}
                />
            ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#a78bfa] via-[#5b21b6] to-[#3c1361]" />
            )}

            {/* Overlay for legibility */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/85 pointer-events-none" />

            {/* "N PROD" badge top-right */}
            {typeof count === "number" && count > 0 && (
                <div
                    className="absolute top-3 right-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#0d0418]/85 border border-[#e879f9]/80 shadow-[0_0_12px_rgba(232,121,249,0.5)]"
                    data-testid={`category-count-${category.slug}`}
                >
                    <span className="font-mono uppercase tracking-wider text-[10px] font-bold text-[#e879f9]">
                        {count} prod
                    </span>
                </div>
            )}

            {/* Category name + circular arrow */}
            <div className="absolute inset-x-0 bottom-0 px-4 pb-3 flex items-end justify-between gap-2">
                <h3
                    className="font-display tracking-wide text-white text-base sm:text-lg uppercase leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.85)]"
                    data-testid={`category-name-${category.slug}`}
                >
                    {category.name}
                </h3>
                <button
                    onClick={(e) => { e.stopPropagation(); onOpen?.(); }}
                    className="shrink-0 w-9 h-9 rounded-full bg-[#7c3aed] hover:bg-[#e879f9] border border-white/20 shadow-[0_0_14px_rgba(232,121,249,0.55)] flex items-center justify-center active:scale-95 transition-all"
                    data-testid={`category-open-${category.slug}`}
                    aria-label={`Apri ${category.name}`}
                >
                    <ChevronRight className="w-4 h-4 text-white" />
                </button>
            </div>
        </motion.article>
    );
};

export default CategoryCard;
